import React, { useEffect } from 'react';
import { Form, Input, InputNumber, Switch, Row, Col, Spin } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { Button as CrcButton } from '@crc-evans/ui-components';
import { useArcWeldingFormOptions } from '../../hooks/useArcWeldingFormOptions';
import { AwFormSelect } from './ArcWeldingListShell';
import { AW_FORM_LABELS, AW_MODAL_SECTIONS } from '../../utils/arcWeldingVerificationUi';
import './ArcWelding.css';

const { TextArea } = Input;

const ArcWeldingCriteriaForm = ({ open, initialValues, onCancel, onSubmit, loading }) => {
  const [form] = Form.useForm();
  const { options } = useArcWeldingFormOptions();

  useEffect(() => {
    if (open) {
      form.setFieldsValue({
        isActive: true,
        displayOrder: 0,
        ...initialValues,
      });
    } else {
      form.resetFields();
    }
  }, [open, initialValues, form]);

  if (!open) return null;

  const handleSave = async () => {
    const values = await form.validateFields();
    onSubmit(values);
  };

  const pageTitle = initialValues?.id ? 'Edit Verification Criterion' : 'New Verification Criterion';
  const subtitle = [initialValues?.parameter, initialValues?.method].filter(Boolean).join(' · ')
    || 'IEC 60974-14 tolerance rule';

  return (
    <Spin spinning={Boolean(loading)}>
      <div className="aw-certificate-form aw-admin-form-page heat-profile-content-wrapper">
        <div className="aw-form-page-header">
          <div className="aw-form-page-header-top">
            <div className="aw-form-page-title-group">
              <h1 className="aw-form-page-title">{pageTitle}</h1>
              <p className="aw-form-page-subtitle">{subtitle}</p>
            </div>
          </div>
          <div className="aw-form-page-header-bottom">
            <button
              type="button"
              className="heat-profile-page-back-link aw-form-back-btn"
              onClick={onCancel}
            >
              <span className="heat-profile-back-link">
                <ArrowLeftOutlined /> Back to List
              </span>
            </button>
            <div className="heat-profile-header-actions calibration-modern-actions aw-form-header-actions">
              <CrcButton label="Cancel" variant="tertiary" size="small" onClick={onCancel} />
              <CrcButton
                label={loading ? 'Saving…' : 'Save'}
                variant="primary"
                size="small"
                onClick={handleSave}
                disabled={loading}
              />
            </div>
          </div>
        </div>

        <Form form={form} layout="vertical" className="aw-admin-form-fields">
          <div className="aw-section aw-section--form">
            <div className="aw-section-title">{AW_MODAL_SECTIONS.CRITERION}</div>
            <Row gutter={[16, 8]}>
              <Col xs={24} md={12}>
                <Form.Item name="parameter" label={AW_FORM_LABELS.parameter} rules={[{ required: true }]}>
                  <AwFormSelect
                    options={options.parameter}
                    placeholder="Select Parameter"
                    showSearch
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="method" label={AW_FORM_LABELS.method} rules={[{ required: true }]}>
                  <AwFormSelect
                    options={options.method}
                    placeholder="Select Method"
                    showSearch
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="grade" label={AW_FORM_LABELS.grade} rules={[{ required: true }]}>
                  <AwFormSelect
                    options={options.grade}
                    placeholder="Select Grade"
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="rangeCondition" label={AW_FORM_LABELS.rangeCondition}>
                  <Input placeholder="e.g. Low, High, All" />
                </Form.Item>
              </Col>
            </Row>
          </div>

          <div className="aw-section aw-section--form">
            <div className="aw-section-title">{AW_MODAL_SECTIONS.TOLERANCE}</div>
            <Row gutter={[16, 8]}>
              <Col xs={24} md={12}>
                <Form.Item name="toleranceType" label={AW_FORM_LABELS.toleranceType}>
                  <AwFormSelect
                    options={options.toleranceType}
                    placeholder="Select Tolerance Type"
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="toleranceValue" label={AW_FORM_LABELS.toleranceValue}>
                  <InputNumber className="aw-full-width-input" placeholder="Value" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="toleranceBasis" label={AW_FORM_LABELS.toleranceBasis}>
                  <Input placeholder="Enter Tolerance Basis" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="lowRangeThresholdPct" label={AW_FORM_LABELS.lowRangeThresholdPct}>
                  <InputNumber className="aw-full-width-input" placeholder="%" min={0} max={100} />
                </Form.Item>
              </Col>
            </Row>
          </div>

          <div className="aw-section aw-section--form">
            <div className="aw-section-title">{AW_MODAL_SECTIONS.DISPLAY}</div>
            <Row gutter={[16, 8]}>
              <Col xs={24} sm={12}>
                <Form.Item name="displayOrder" label={AW_FORM_LABELS.displayOrder}>
                  <InputNumber className="aw-full-width-input" placeholder="0" min={0} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="isActive"
                  label={AW_FORM_LABELS.active}
                  valuePropName="checked"
                  className="aw-form-switch-item"
                >
                  <Switch
                    className="aw-active-switch"
                    checkedChildren="Yes"
                    unCheckedChildren="No"
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item name="notes" label={AW_FORM_LABELS.notes}>
                  <TextArea rows={3} placeholder="Optional Notes" />
                </Form.Item>
              </Col>
            </Row>
          </div>
        </Form>
      </div>
    </Spin>
  );
};

export default ArcWeldingCriteriaForm;
